import type { SourceRange } from "@magic-md/core";

export function offsetToSourceRange(
    content: string,
    startOffset: number,
    endOffset: number,
): SourceRange {
    const starts = computeLineStarts(content);
    const start = offsetToPoint(content, starts, startOffset);
    const end = offsetToPoint(content, starts, endOffset);
    return {
        startLine: start.line,
        startColumn: start.column,
        endLine: end.line,
        endColumn: end.column,
    };
}

export function getSelectionContext(
    content: string,
    startOffset: number,
    endOffset: number,
): { quote: string; prefix?: string; suffix?: string } {
    const start = clampOffset(content, startOffset);
    const end = clampOffset(content, endOffset);
    const from = Math.min(start, end);
    const to = Math.max(start, end);
    const prefixStart = Math.max(0, from - 32);
    const suffixEnd = Math.min(content.length, to + 32);
    return {
        quote: content.slice(from, to),
        prefix: content.slice(prefixStart, from) || undefined,
        suffix: content.slice(to, suffixEnd) || undefined,
    };
}

function computeLineStarts(content: string): number[] {
    const starts = [0];
    for (let i = 0; i < content.length; i++) {
        if (content[i] === "\n") starts.push(i + 1);
    }
    return starts;
}

function offsetToPoint(
    content: string,
    starts: number[],
    rawOffset: number,
): { line: number; column: number } {
    const offset = clampOffset(content, rawOffset);
    let low = 0;
    let high = starts.length - 1;
    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const start = starts[mid] ?? 0;
        const nextStart = starts[mid + 1] ?? Number.POSITIVE_INFINITY;
        if (offset < start) {
            high = mid - 1;
        } else if (offset >= nextStart) {
            low = mid + 1;
        } else {
            return { line: mid + 1, column: offset - start };
        }
    }
    const lastLine = Math.max(0, starts.length - 1);
    return {
        line: lastLine + 1,
        column: offset - (starts[lastLine] ?? 0),
    };
}

function clampOffset(content: string, offset: number): number {
    if (!Number.isFinite(offset)) return 0;
    return Math.max(0, Math.min(content.length, Math.floor(offset)));
}
